import { useMemo } from 'react';
import { useI18n } from '../contexts/I18nProvider';
import { getLocalizedEnvVar, getLocalizedEnvVarBoolean } from '../utils/languageDetection';

/**
 * Hook to get language-aware environment variables
 * @returns Object with methods to get localized environment variables
 */
export function useLocalizedEnv() {
  const { currentLanguage } = useI18n();

  return useMemo(() => ({
    /**
     * Get a localized environment variable as string
     * @param baseKey - The base environment variable key
     * @param fallback - Optional fallback value
     * @returns The environment variable value for the current language or fallback
     */
    getVar: (baseKey: string, fallback?: string): string | undefined => {
      return getLocalizedEnvVar(baseKey, currentLanguage, fallback);
    },

    /**
     * Get a localized environment variable as boolean
     * @param baseKey - The base environment variable key
     * @param fallback - Optional fallback boolean value
     * @returns The boolean environment variable value for the current language or fallback
     */
    getBoolean: (baseKey: string, fallback?: boolean): boolean => {
      return getLocalizedEnvVarBoolean(baseKey, currentLanguage, fallback);
    },

    /**
     * Get multiple localized environment variables at once
     * @param keys - Array of base environment variable keys
     * @returns Object with all the localized values
     */
    getMultiple: (keys: string[]): Record<string, string | undefined> => {
      return keys.reduce((acc, key) => {
        acc[key] = getLocalizedEnvVar(key, currentLanguage);
        return acc;
      }, {} as Record<string, string | undefined>);
    },

    /**
     * Current language being used for localization
     */
    currentLanguage,
  }), [currentLanguage]);
}